import { useModal } from '@ebay/nice-modal-react';
import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as Delete } from 'assets/icons/Delete.svg';
import { ReactComponent as Leave } from 'assets/icons/Leave.svg';
import ConfirmationModal from 'components/ConfirmationModal';
import { boldElement } from 'components/ConfirmPopup';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useLeaveWorkspace from 'hooks/useLeaveWorkspace/useLeaveWorkspace';
import { DefaultRoleTypesEnum } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { getJoinedMemberCount } from 'utils/matrixUtils';

import LeaveAndAssignAdminModal from './LeaveAndAssignAdminModal';
import ManagementActionSection from './ManagementActionSection';
import WorkspaceSettingsModal from '../../WorkspaceSettingsModal';

const getMessage = (workspaceName: string, isDelete = false) =>
  isDelete ? (
    <Trans
      i18nKey="workspaces.leave_and_delete_question"
      components={{ bold: boldElement }}
      values={{ workspaceName }}
    />
  ) : (
    <Trans
      i18nKey="workspaces.leave_workspace_question"
      components={{ bold: boldElement }}
      values={{ workspaceName }}
    />
  );

const WorkspaceLeaveSetting = () => {
  const { t } = useTranslation();
  const confirmationModal = useModal(ConfirmationModal);
  const { remove: removeWorkspaceSettingsModal } = useModal(WorkspaceSettingsModal);
  const leaveAndAssignAdminModal = useModal(LeaveAndAssignAdminModal);
  const { matrixClient } = useMatrixContext();
  const { leaveWorkspace } = useLeaveWorkspace();
  const { workspaceId, setWorkspaceId, workspacesState, workspaceRoles, roleAssignments } = useWorkspacesContext({
    requireWorkspaceId: true,
  });
  const { pushToast } = useToast();

  const workspaceName = workspacesState.workspaces[workspaceId]?.name;
  const workspaceRoom = matrixClient.getRoom(workspaceId);
  const memberCount = getJoinedMemberCount(workspaceRoom);
  const isAlone = memberCount === 1;
  const currentUserId = matrixClient.getUserId();
  const ownerRole = workspaceRoles.find(({ defaultRoleType }) => defaultRoleType === DefaultRoleTypesEnum.OWNER);
  const isOwnerRole = roleAssignments.some(
    ({ matrixId, roleId }) => matrixId === currentUserId && roleId === ownerRole?.id
  );

  const handleLeaveWorkspace = async () => {
    try {
      await leaveWorkspace(workspaceId);
      setWorkspaceId(null);
      removeWorkspaceSettingsModal();
      logger.info('Successfully left workspace', { workspaceId });
      pushToast({ variant: 'success', title: t('workspaces.left_workspace_success') });
    } catch (error) {
      logger.error('Error while leaving workspace', { workspaceId, error });
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
    }
  };

  const displayModal = () => {
    if (isAlone) {
      return confirmationModal.show({
        icon: Delete,
        message: getMessage(workspaceName, true),
        confirmButtonText: t('workspaces.leave_and_delete'),
        onConfirm: handleLeaveWorkspace,
      });
    }

    if (isOwnerRole) return leaveAndAssignAdminModal.show();

    confirmationModal.show({
      icon: Leave,
      message: getMessage(workspaceName),
      confirmButtonText: t('leave'),
      onConfirm: handleLeaveWorkspace,
    });
  };

  return (
    <ManagementActionSection
      title={t('workspaces.leave_workspace')}
      description={t('workspaces.leave_workspace_desc')}
      buttonIcon={Leave}
      onClick={displayModal}
    />
  );
};

export default WorkspaceLeaveSetting;
